import type { AxiosError } from 'axios'

export type DuplicateCandidate = { id: number; name: string }

/** Extract `response.data.detail` from an Axios-shaped error. */
export function getApiErrorDetail(error: unknown): unknown {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError<{ detail?: unknown }>
    return axiosError.response?.data?.detail
  }
  return undefined
}

function getAxiosResponseData(error: unknown): Record<string, unknown> | null {
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as AxiosError).response?.data
    if (data && typeof data === 'object') {
      return data as Record<string, unknown>
    }
  }
  return null
}

function parseCandidateItem(item: unknown): DuplicateCandidate | null {
  if (item == null || typeof item !== 'object') return null
  const { id, name } = item as { id?: unknown; name?: unknown }
  if (typeof name !== 'string') return null
  const numId = typeof id === 'number' ? id : Number(id)
  if (!Number.isFinite(numId) || Number.isNaN(numId)) return null
  return { id: numId, name }
}

function extractCandidatesArray(source: unknown): unknown[] | null {
  if (!source || typeof source !== 'object' || !('candidates' in source)) return null
  const candidates = (source as { candidates?: unknown }).candidates
  if (!Array.isArray(candidates) || candidates.length === 0) return null
  return candidates
}

function parseCandidatesFromArray(candidates: unknown[]): DuplicateCandidate[] | null {
  const valid: DuplicateCandidate[] = []
  for (const item of candidates) {
    const parsed = parseCandidateItem(item)
    if (parsed) valid.push(parsed)
  }
  return valid.length > 0 ? valid : null
}

function parseCandidatesFromSource(source: unknown): DuplicateCandidate[] | null {
  const candidates = extractCandidatesArray(source)
  if (!candidates) return null
  return parseCandidatesFromArray(candidates)
}

function hasCandidatesKey(source: unknown): boolean {
  return source != null && typeof source === 'object' && 'candidates' in source
}

/** Parse FastAPI duplicate-guard `candidates` from an Axios-shaped error. */
export function parseDuplicateCandidates(error: unknown): DuplicateCandidate[] | null {
  const data = getAxiosResponseData(error)
  if (!data) return null

  const fromDetail = parseCandidatesFromSource(data.detail)
  if (fromDetail) return fromDetail

  if (typeof data.detail === 'string') {
    try {
      const parsed = JSON.parse(data.detail) as unknown
      const fromString = parseCandidatesFromSource(parsed)
      if (fromString) return fromString
    } catch {
      // not JSON
    }
  }

  const fromData = parseCandidatesFromSource(data)
  if (fromData) return fromData

  return null
}

function duplicateCandidatesMessage(candidates: DuplicateCandidate[]): string {
  return `Similar product already exists: ${candidates.map((c) => c.name).join(', ')}`
}

/** Format FastAPI / Axios error payloads for display. */
export function formatApiError(error: unknown, fallback = 'Request failed'): string {
  const duplicateCandidates = parseDuplicateCandidates(error)
  if (duplicateCandidates) {
    return duplicateCandidatesMessage(duplicateCandidates)
  }

  const detail = getApiErrorDetail(error)
  if (typeof detail === 'string') {
    try {
      const parsed = JSON.parse(detail) as unknown
      if (hasCandidatesKey(parsed)) {
        const fromString = parseCandidatesFromSource(parsed)
        if (fromString) return duplicateCandidatesMessage(fromString)
        return 'Similar product already exists'
      }
    } catch {
      // not JSON
    }
    return detail
  }
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item) {
          return String(item.msg)
        }
        return JSON.stringify(item)
      })
      .join('; ')
  }
  if (detail && typeof detail === 'object') {
    if (hasCandidatesKey(detail)) {
      return 'Similar product already exists'
    }
    return JSON.stringify(detail)
  }

  const data = getAxiosResponseData(error)
  if (data && hasCandidatesKey(data)) {
    return 'Similar product already exists'
  }

  if (error instanceof Error) {
    return error.message
  }
  return fallback
}
